// src/services/stripe.ts
// Stripe en Go Safely (19-sep-2026):
//   - CUOTA DEL CHOFER con tarjeta → pago único que abre el SERVIDOR
//     (crearPagoCuota en functions/src/pagoCuotaStripe.ts, vía la extensión
//     firestore-stripe-payments). Aquí no hay nada de eso: la app solo llama a
//     la callable y abre la página de Stripe (wallet.tsx).
//   - Lo que queda aquí: la lista de planes recurrentes de la extensión
//     (apagada por SUSCRIPCION_STRIPE_ENABLED) y la escucha de suscripciones.
//   - Los tres caminos que le pedían cobros a Railway (recarga con tarjeta,
//     guardar tarjeta, alta en Stripe Connect) se RETIRARON: Railway está
//     borrado y responden 404. Nada los llamaba ya.

import { db } from './firebaseConfig';
import { addDoc, collection, doc, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
// SDK NATIVO — imprescindible en móvil: el SDK web de Firestore va ANÓNIMO
// cuando la sesión vive en @react-native-firebase/auth, así que las reglas
// que exigen `isSelf(uid)` deniegan la escritura. Mismo patrón que usa
// firestoreService.ts.
import nativeFirestore from '@react-native-firebase/firestore';
import { Platform } from 'react-native';

export const StripeService = {
  /**
   * Crea una suscripción usando la Firebase Stripe Extension.
   * La extension escucha en `customers/{uid}/checkout_sessions/{id}` y al
   * momento de crear el doc, genera la URL de Checkout en el mismo doc.
   *
   * Devuelve la URL — el caller decide cómo abrirla (window.location en web,
   * WebBrowser.openBrowserAsync en nativo).
   */
  async createSubscription(customerId: string, priceId: string): Promise<string | null> {
    if (!customerId) {
      console.warn('[Stripe] createSubscription sin customerId');
      return null;
    }
    if (!priceId) {
      console.warn('[Stripe] createSubscription sin priceId');
      return null;
    }

    const successUrl =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? `${window.location.origin}/wallet?success=true`
        : 'https://gosafely-3e0e7.web.app/wallet?success=true';
    const cancelUrl =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? `${window.location.origin}/wallet?canceled=true`
        : 'https://gosafely-3e0e7.web.app/wallet?canceled=true';

    const payload = {
      price: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
    };

    // ── RAMA NATIVA ────────────────────────────────────────────────────────
    // OBLIGATORIA. El SDK web de Firestore va ANÓNIMO en React Native (la
    // sesión vive en el SDK nativo), así que la regla `isSelf(customerId)`
    // de /customers/{uid}/checkout_sessions denegaba la escritura y esta
    // función fallaba siempre en el teléfono. Por eso la extensión llevaba
    // instalada desde junio sin poder cobrar una sola membresía.
    if (Platform.OS !== 'web') {
      const ref = await nativeFirestore()
        .collection('customers')
        .doc(customerId)
        .collection('checkout_sessions')
        .add(payload);

      return await new Promise<string | null>((resolve) => {
        const timer = setTimeout(() => {
          unsub();
          console.warn('[Stripe] la extensión no respondió en 30s (nativo)');
          resolve(null);
        }, 30000);

        const unsub = ref.onSnapshot(
          (snap: any) => {
            const data = snap?.data?.();
            if (!data) return;
            if (data.error) {
              clearTimeout(timer);
              unsub();
              console.error('[Stripe Extension] error:', data.error?.message ?? data.error);
              resolve(null);
            } else if (data.url) {
              clearTimeout(timer);
              unsub();
              resolve(String(data.url));
            }
          },
          (err: any) => {
            clearTimeout(timer);
            // FUGA CERRADA — Esta rama resolvía la promesa y dejaba el listener
            // VIVO: un error aquí (regla, red) no termina la suscripción del
            // lado del cliente, así que cada intento de pagar la membresía
            // dejaba otro escucha pegado al documento para el resto de la
            // sesión. El camino feliz sí llamaba a unsub(); este no.
            try { unsub(); } catch { /* ya estaba cerrada */ }
            console.error('[Stripe] onSnapshot checkout_session falló:', err?.message);
            resolve(null);
          }
        );
      });
    }

    // ── RAMA WEB (panel del owner Rent Car) ────────────────────────────────
    const checkoutCol = collection(db, `customers/${customerId}/checkout_sessions`);
    const checkoutRef = await addDoc(checkoutCol, payload);

    return await new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => {
        unsub();
        console.warn('[Stripe] La extension no respondió en 30s para checkout_sessions/' + checkoutRef.id);
        resolve(null);
      }, 30000);

      const unsub = onSnapshot(doc(db, checkoutRef.path), (snap) => {
        const data = snap.data();
        if (!data) return;
        if (data.error) {
          clearTimeout(timer);
          unsub();
          console.error('[Stripe Extension] error:', data.error);
          resolve(null);
        } else if (data.url) {
          clearTimeout(timer);
          unsub();
          resolve(data.url as string);
        }
      }, (err) => {
        // Sin esta rama, un fallo de la suscripción dejaba al dueño mirando el
        // spinner los 30 s completos del temporizador antes de rendirse, y el
        // listener seguía vivo mientras tanto.
        clearTimeout(timer);
        try { unsub(); } catch { /* ya estaba cerrada */ }
        console.error('[Stripe] onSnapshot checkout_session (web) falló:', err?.message);
        resolve(null);
      });
    });
  },

  /**
   * Lista los PLANES DE MEMBRESÍA disponibles, leídos del catálogo que la
   * extensión de Stripe sincroniza a Firestore.
   *
   * Fuente de verdad: el panel de Stripe. Creas ahí un Producto con su Precio
   * recurrente y aparece solo en la app — sin recompilar ni publicar versión.
   *
   * Devuelve [] si no hay nada configurado todavía; el caller debe mostrar un
   * mensaje honesto en vez de una lista vacía.
   */
  async listSubscriptionPlans(): Promise<
    Array<{ productId: string; priceId: string; nombre: string; descripcion: string; monto: number; moneda: string; intervalo: string }>
  > {
    const planes: Array<any> = [];
    try {
      if (Platform.OS !== 'web') {
        const prods = await nativeFirestore()
          .collection('products')
          .where('active', '==', true)
          .get();
        for (const p of prods.docs) {
          const precios = await p.ref.collection('prices').where('active', '==', true).get();
          for (const pr of precios.docs) {
            const d: any = pr.data();
            // Solo precios recurrentes: una membresía no es un pago único.
            if (!d?.interval) continue;
            planes.push({
              productId: p.id,
              priceId: pr.id,
              nombre: String((p.data() as any)?.name ?? 'Plan'),
              descripcion: String((p.data() as any)?.description ?? ''),
              monto: Number(d.unit_amount ?? 0) / 100,
              moneda: String(d.currency ?? 'usd').toUpperCase(),
              intervalo: String(d.interval),
            });
          }
        }
      }
    } catch (e: any) {
      console.warn('[Stripe] listSubscriptionPlans falló:', e?.message);
    }
    return planes.sort((a, b) => a.monto - b.monto);
  },

  /**
   * Solicita un payout desde la cuenta conectada del chofer.
   *
   * ⚠️ APAGADO EN EL SERVIDOR (15-sep-2026). Go Safely no procesa retiros:
   * el pago del viaje es directo entre pasajero y chofer (decision del
   * fundador) y Railway esta eliminado. `requestPayoutSecure` ahora rechaza
   * TODA llamada con failed-precondition. Este metodo se queda solo porque
   * borrarlo no anade seguridad (el servidor ya dice que no) y alguna
   * herramienta vieja lo referencia; NO lo conectes a ninguna pantalla.
   *
   * IMPORTANTE: pasa por la Cloud Function `requestPayoutSecure` que VALIDA
   * que el `stripeAccountId` realmente pertenezca al uid del caller. Llamar
   * a Railway directo desde el cliente sería un agujero — cualquiera podría
   * pasar el `stripeAccountId` de OTRO chofer y desviar sus fondos.
   *
   * El argumento `stripeAccountId` queda en la firma por compatibilidad
   * pero se ignora: la callable lo lee server-side desde /users/{uid}.
   */
  async requestPayout(_ignoredStripeAccountId: string, amount: number, currency: string = 'dop'): Promise<boolean> {
    try {
      const { callRequestPayoutSecure } = await import('./cloudFunctions');
      const resp = await callRequestPayoutSecure({ amount, currency });
      return resp.data?.success === true;
    } catch (e: any) {
      console.error('[Stripe] requestPayout fallo:', e?.code, e?.message);
      return false;
    }
  },

  /**
   * Escucha el estado de la suscripción activa del usuario (creada por la extension).
   * Devuelve unsubscribe. La extension escribe a customers/{uid}/subscriptions.
   */
  listenToActiveSubscription(customerId: string, callback: (sub: any | null) => void) {
    const subsCol = collection(db, `customers/${customerId}/subscriptions`);
    const q = query(subsCol, orderBy('created', 'desc'), limit(1));
    return onSnapshot(q, (snap) => {
      if (snap.empty) {
        callback(null);
        return;
      }
      const d = snap.docs[0];
      callback({ id: d.id, ...d.data() });
    });
  },
};
